import {
  amountText,
  BOTTLE_LIMITS,
  BOTTLE_STEP,
  BOTTLE_UNIT_NAME,
  type BottleUnit,
  roundAmount,
} from "@babia/checks";
import { useEffect, useState } from "react";
import { isLaterThanNow } from "../../../lib/entry-edit.ts";
import { forAllWords, TYPE_EMOJI } from "../../../lib/entry-text.ts";
import { whenText } from "../../../lib/revisions.ts";
import { cn } from "../../../lib/utils.ts";
import { Button } from "../../ui/button.tsx";
import { Sheet } from "../../ui/sheet.tsx";
import { TimeField } from "./time-field.tsx";

/** What the – and + buttons say they do, by unit. */
const STEP_NAME: Record<BottleUnit, string> = { oz: "Half an ounce", ml: "10 ml" };

/**
 * When and how much the baby drank, laid out as the edit sheet shows a
 * bottle: the time (now, until a parent moves it), then a stepper in the
 * unit's steps (half ounces, or 10 ml) between its limits, the household's
 * preset chips, and the usual amount to start from. Everything here is in
 * `unit`; the caller converts the saved amount to stored ounces. With two or
 * more babies, a second button under Save logs the same bottle for every baby
 * (#89); the first one keeps its one tap.
 */
export function BottleSheet({
  open,
  babyName,
  babyCount,
  unit,
  usual,
  presets,
  now,
  onClose,
  onSave,
  onSaveAll,
}: {
  open: boolean;
  babyName: string;
  /** Every baby in the household: with one, there is no second button. */
  babyCount: number;
  unit: BottleUnit;
  /** Where the stepper opens, in `unit`. */
  usual: number;
  /** The chips, in `unit`. */
  presets: number[];
  now: Date;
  onClose: () => void;
  /** The amount picked, in `unit`, and the time it was drunk. */
  onSave: (amount: number, at: Date) => void;
  /** The amount picked, in `unit`, and its time, for every baby at once. */
  onSaveAll?: (amount: number, at: Date) => void;
}) {
  const [amount, setAmount] = useState(usual);
  // The time picked; null follows the clock, so an untouched time saves now.
  const [at, setAt] = useState<Date | null>(null);
  // Each opening starts from the usual amount, at now.
  useEffect(() => {
    if (open) setAmount(usual);
  }, [open, usual]);
  useEffect(() => {
    if (open) setAt(null);
  }, [open]);
  const shown = at ?? now;
  // iOS's wheel ignores `max`, so a time past now is caught here.
  const problem = at && isLaterThanNow(at, now) ? "That time is later than now" : null;
  // An untouched time is the tap's own moment, not the clock's last tick.
  const save = (send: (amount: number, at: Date) => void) => send(amount, at ?? new Date());

  return (
    <Sheet
      open={open}
      title={`Bottle — ${babyName}`}
      emoji={TYPE_EMOJI.feed}
      onClose={onClose}
      className="max-h-[92dvh] overflow-y-auto overscroll-contain"
    >
      <TimeField label="Time" value={shown} max={now} now={now} onChange={setAt} />
      <AmountStepper
        unit={unit}
        amount={amount}
        usual={usual}
        presets={presets}
        onChange={setAmount}
      />
      {problem ? (
        <p className="mt-3 text-center text-[13px] text-accent-2" role="status">
          {problem}
        </p>
      ) : null}
      <Button
        variant="primary"
        className="mt-4 w-full"
        disabled={!!problem}
        onClick={() => save(onSave)}
      >
        Save at {whenText(shown, now)}
      </Button>
      {onSaveAll && babyCount > 1 ? (
        <Button
          variant="secondary"
          className="mt-2 w-full"
          disabled={!!problem}
          onClick={() => save(onSaveAll)}
        >
          Save for {forAllWords(babyCount)} at {whenText(shown, now)}
        </Button>
      ) : null}
    </Sheet>
  );
}

/**
 * The bottle stepper and its preset chips, in `unit`; the bottle sheet and
 * the edit sheet share it. A bottle saved without an amount shows "–", and
 * its first step starts from `usual`.
 */
export function AmountStepper({
  unit,
  amount,
  usual,
  presets,
  onChange,
}: {
  unit: BottleUnit;
  amount: number | null;
  usual: number;
  presets: number[];
  onChange: (amount: number) => void;
}) {
  const step = BOTTLE_STEP[unit];
  const { min, max } = BOTTLE_LIMITS[unit];
  const bump = (delta: number) =>
    onChange(
      amount === null ? usual : roundAmount(Math.min(max, Math.max(min, amount + delta)), unit),
    );

  return (
    <>
      <div className="mt-[18px] flex items-center justify-center gap-[22px]">
        <Button
          variant="stepper"
          onClick={() => bump(-step)}
          aria-label={`${STEP_NAME[unit]} less`}
        >
          –
        </Button>
        <div className="min-w-[120px] text-center">
          <div
            className="heading text-[64px] leading-none tracking-[-0.04em] tabular-nums"
            aria-live="polite"
          >
            {amount ?? "–"}
          </div>
          <div className="kicker mt-1 text-[12px]">{BOTTLE_UNIT_NAME[unit]}</div>
        </div>
        <Button variant="stepper" onClick={() => bump(step)} aria-label={`${STEP_NAME[unit]} more`}>
          +
        </Button>
      </div>
      <div className="mt-[18px] flex gap-2">
        {presets.map((p) => (
          <Button
            key={p}
            variant="chip"
            className={cn(amount === p && "is-selected")}
            aria-pressed={amount === p}
            onClick={() => onChange(p)}
          >
            {amountText(p, unit)}
          </Button>
        ))}
      </div>
    </>
  );
}
